import React, { useState, useEffect, useCallback } from 'react';
import { IPTVProvider, useIPTV } from './context/IPTVContext';
import { TVNavbar } from './components/navigation/TVNavbar';
import { VideoPlayer } from './components/player/VideoPlayer';
import { LiveChannelsView } from './components/live/LiveChannelsView';
import { MoviesView } from './components/vod/MoviesView';
import { SeriesView } from './components/vod/SeriesView';
import { FavoritesView } from './components/vod/FavoritesView';
import { HomeView } from './components/home/HomeView';
import { ProfileSelectorModal } from './components/profiles/ProfileSelectorModal';
import { ParentalPinModal } from './components/parental/ParentalPinModal';
import { AuthModal } from './components/auth/AuthModal';
import { ToastNotifications } from './components/common/ToastNotifications';
import { QuickSearchModal } from './components/common/QuickSearchModal';
import { ScreenLimitModal } from './components/common/ScreenLimitModal';
import { ScreenKickedModal } from './components/common/ScreenKickedModal';
import { ProfileOnTvModal } from './components/common/ProfileOnTvModal';
import { CastBridge } from './components/player/CastBridge';
import { CastRemote } from './components/player/CastRemote';
import { useTVNavigation } from './utils/tvNavigation';
import { lockPortraitOrientation, unlockOrientationForVideo } from './utils/orientation';
import { enableScreenWakeLock, disableScreenWakeLock } from './utils/screenWakeLock';
import { applyProfileTheme } from './utils/themeColors';
import { Play, Maximize2, Tv, Radio, ChevronUp, ChevronDown, Sparkles, Smartphone } from 'lucide-react';
import { OpenTvLoader } from './components/common/OpenTvLoader';
import { AvisosTela } from './components/common/AvisosTela';
import { apiClient } from './lib/apiClient';

// Code-splitting / Lazy loading para módulos e telas secundárias pesadas
const SettingsModal = React.lazy(() => import('./components/settings/SettingsModal').then(m => ({ default: m.SettingsModal })));
const ConnectedDevicesModal = React.lazy(() => import('./components/settings/ConnectedDevicesModal').then(m => ({ default: m.ConnectedDevicesModal })));
const GerenciadorPage = React.lazy(() => import('./components/admin/GerenciadorPage').then(m => ({ default: m.GerenciadorPage })));
// V8.8.4: o receptor da TV é só a página public/tv/index.html (o TvReceiver.tsx em React saiu de uso).

const IPTVAppContent: React.FC = () => {
  useTVNavigation(true);
  const {
    activeTab,
    currentChannel,
    currentMovie,
    currentSeries,
    currentEpisode,
    stopPlayback,
    isPlaying,
    isPlayerControlsVisible,
    pokePlayerControls,
    authUser,
    currentProfile,
    currentProfileId,
    isProfileKicked,
    isAdmin,
    isAuthModalOpen,
    setIsAuthModalOpen,
    isDevicesModalOpen,
    closeDevicesModal,
  } = useIPTV();

  const [profilesModalOpen, setProfilesModalOpen] = useState(false);
  const [pinProfileForModal, setPinProfileForModal] = useState<any>(null);
  const [profilesManageMode, setProfilesManageMode] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  
  // Controle de página: exige seleção de perfil na inicialização antes de montar o catálogo
  const [hasSelectedInitialProfile, setHasSelectedInitialProfile] = useState(false);

  // Fora do perfil (na tela inicial "Quem está assistindo?" ou trocando perfil), tema é o amarelo do sistema (#f59e0b)
  useEffect(() => {
    if (!hasSelectedInitialProfile || profilesModalOpen) {
      applyProfileTheme('#f59e0b');
    }
  }, [hasSelectedInitialProfile, profilesModalOpen]);

  useEffect(() => {
    if (!authUser) {
      setHasSelectedInitialProfile(false);
      setProfilesModalOpen(false);
      setIsAuthModalOpen(true);
    } else if (!currentProfileId || isProfileKicked) {
      setHasSelectedInitialProfile(false);
      setProfilesModalOpen(false);
    }
  }, [authUser, currentProfileId, isProfileKicked, setIsAuthModalOpen]);

  const handleOpenProfiles = (targetProfile?: any, startInManage?: boolean) => {
    setPinProfileForModal(targetProfile || null);
    setProfilesManageMode(!!startInManage);
    setProfilesModalOpen(true);
  };

  const handleCloseProfilesModal = useCallback(() => {
    setProfilesModalOpen(false);
    setPinProfileForModal(null);
    setProfilesManageMode(false);
    if (currentProfile?.color) {
      applyProfileTheme(currentProfile.color);
    }
  }, [currentProfile?.color]);

  const hasActivePlayback = !!(currentChannel || currentMovie || (currentSeries && currentEpisode));

  // Detecção de orientação no celular para travar o catálogo em vertical
  const [isLandscapeMobileInCatalog, setIsLandscapeMobileInCatalog] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      if (typeof window === 'undefined') return;
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallHeight = window.innerHeight < 550;
      const isLandscape = window.innerWidth > window.innerHeight;

      // Se for celular touch em modo paisagem SEM vídeo em execução
      if (!hasActivePlayback && isTouch && isLandscape && isSmallHeight) {
        setIsLandscapeMobileInCatalog(true);
      } else {
        setIsLandscapeMobileInCatalog(false);
      }
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [hasActivePlayback]);

  // Travar rotação em vertical (portrait) nos menus/catálogos, e liberar rotação apenas com o vídeo em execução
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (hasActivePlayback) {
        document.body.classList.add('is-video-playing');
        unlockOrientationForVideo();
        enableScreenWakeLock();
      } else {
        document.body.classList.remove('is-video-playing');
        lockPortraitOrientation();
        disableScreenWakeLock();
      }
    }
  }, [hasActivePlayback]);

  // Re-assegura WakeLock com transient activation no iOS Safari a qualquer toque do usuário enquanto o vídeo estiver em execução
  useEffect(() => {
    if (!hasActivePlayback) return;

    const onUserInteraction = () => {
      enableScreenWakeLock();
    };

    window.addEventListener('touchend', onUserInteraction, { passive: true, capture: true });
    window.addEventListener('pointerup', onUserInteraction, { passive: true, capture: true });
    window.addEventListener('click', onUserInteraction, { passive: true, capture: true });

    return () => {
      window.removeEventListener('touchend', onUserInteraction, { capture: true });
      window.removeEventListener('pointerup', onUserInteraction, { capture: true });
      window.removeEventListener('click', onUserInteraction, { capture: true });
    };
  }, [hasActivePlayback]);

  // Resetar scroll inicial do viewport no iOS Safari para colar o menu inferior imediatamente
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      const timer = setTimeout(() => window.scrollTo(0, 0), 100);
      return () => clearTimeout(timer);
    }
  }, []);
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      // Don't intercept typing inside text inputs unless it's Escape/Back
      const activeTag = (document.activeElement as HTMLElement)?.tagName;
      const isInput = ['INPUT', 'TEXTAREA'].includes(activeTag);

      // 1. Remote Back / Escape / Backspace handling
      if (e.key === 'Escape' || e.key === 'BrowserBack' || e.key === 'GoBack' || (!isInput && e.key === 'Backspace')) {
        if (searchModalOpen) {
          e.preventDefault();
          setSearchModalOpen(false);
          return;
        }
        if (profilesModalOpen) {
          e.preventDefault();
          setProfilesModalOpen(false);
          return;
        }
        if (settingsModalOpen) {
          e.preventDefault();
          setSettingsModalOpen(false);
          return;
        }
        if (hasActivePlayback) {
          e.preventDefault();
          stopPlayback();
          return;
        }
      }

      if (isInput) return;

      // 2. Search shortcut
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchModalOpen((prev) => !prev);
      } else if (!hasActivePlayback && (e.key === 's' || e.key === 'S' || e.key === '/')) {
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [
    hasActivePlayback,
    searchModalOpen,
    profilesModalOpen,
    settingsModalOpen,
    stopPlayback,
  ]);

  // PÁGINA 1: Se não estiver autenticado, exibe a tela de login em tela cheia limpa
  if (!authUser) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-black text-white font-sans overflow-hidden select-none relative">
        <AuthModal
          isOpen={true}
          onClose={() => {}}
        />
        <ToastNotifications />
      </div>
    );
  }

  // PÁGINA 2: Se autenticado, exige a seleção de perfil em tela cheia antes de carregar o catálogo
  if (!hasSelectedInitialProfile) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-black text-white font-sans overflow-hidden select-none relative">
        <ProfileSelectorModal
          isOpen={true}
          onClose={() => {
            setHasSelectedInitialProfile(true);
            if (currentProfile?.color) {
              applyProfileTheme(currentProfile.color);
            }
          }}
          initialPinProfile={null}
          startInManageMode={false}
        />
        <ToastNotifications />
      </div>
    );
  }

  // PÁGINA 3: Usuário autenticado com perfil selecionado -> Catálogo e Player Principal
  return (
    <div
      className="flex flex-col w-full h-full h-[100dvh] max-h-[100dvh] bg-black text-white font-sans overflow-hidden select-none relative"
    >
      {/* Top TV Navigation Bar: displayed only in catalog/guide mode */}
      {!hasActivePlayback && (
        <TVNavbar
          onOpenProfiles={handleOpenProfiles}
          onOpenSettings={() => setSettingsModalOpen(true)}
          onOpenSearch={() => setSearchModalOpen(true)}
          onOpenAuth={() => setIsAuthModalOpen(true)}
        />
      )}

      {/* Main Content Body */}
      <main className={`flex-1 min-h-0 relative flex flex-col overflow-hidden ${hasActivePlayback ? '' : 'pb-16 lg:pb-0'}`}>
        {hasActivePlayback ? (
          /* When video is actively playing - occupies 100% full screen */
          <div className="w-full h-full relative">
            <VideoPlayer />
          </div>
        ) : (
          /* Catalog and Guide Views */
          <div className="w-full h-full flex flex-col overflow-hidden">
            {activeTab === 'home' && <HomeView />}
            {activeTab === 'live' && <LiveChannelsView />}
            {activeTab === 'movies' && <MoviesView />}
            {activeTab === 'series' && <SeriesView />}
            {activeTab === 'favorites' && <FavoritesView />}
            {activeTab === 'settings' && (
              <div className="flex-1 flex items-center justify-center p-6 text-center">
                <div className="max-w-md">
                  <Tv className="w-16 h-16 text-cyan-400 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-bold text-white mb-2">Painel de Configurações</h3>
                  <p className="text-xs text-zinc-400 mb-4">
                    Abra as configurações para gerenciar conexões M3U, contas Xtream Codes, controle parental e proporção de tela.
                  </p>
                  <button
                    onClick={() => setSettingsModalOpen(true)}
                    className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-2xl text-xs transition cursor-pointer shadow-lg shadow-cyan-900/40"
                  >
                    Abrir Configurações Completas
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals & Overlays */}
      <QuickSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
      />

      <ProfileSelectorModal
        isOpen={profilesModalOpen}
        onClose={handleCloseProfilesModal}
        initialPinProfile={pinProfileForModal}
        startInManageMode={profilesManageMode}
      />

      <React.Suspense fallback={null}>
        {settingsModalOpen && (
          <SettingsModal
            isOpen={settingsModalOpen}
            onClose={() => setSettingsModalOpen(false)}
          />
        )}
      </React.Suspense>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />

      <ParentalPinModal />

      {/* V8.7.5: avisos do Gerenciador (promoções, comunicados) — depois de escolher o perfil, fora do vídeo */}
      <AvisosTela
        ativo={!!authUser && !!currentProfileId && !hasActivePlayback && !profilesModalOpen}
        profileId={currentProfileId}
        plataforma="web"
        buscar={(p, pl, so) => apiClient.avisosPendentes(p, pl, so)}
        buscarMarca={(pl) => apiClient.avisosMarca(pl)}
        marcar={(id, p, a, pl) => apiClient.avisoMarcar(id, p, a, pl)}
      />

      {/* Modal de Limite de Telas Excedido (Multi-Tenant Heartbeat) */}
      <ScreenLimitModal />

      {/* Modal de Aviso de Tela Desconectada por Outro Perfil */}
      <ScreenKickedModal />

      {/* V8.8.6: perfil tocando na TV (Cast) → assistir aqui / enviar para a TV */}
      <ProfileOnTvModal />
      <CastBridge />
      {/* V8.8.7: barra "Transmitindo" + controle remoto completo do Cast */}
      <CastRemote />

      {/* Modal de Gerenciamento de Aparelhos Conectados (Até 5 por Conta) */}
      <React.Suspense fallback={null}>
        {isDevicesModalOpen && (
          <ConnectedDevicesModal
            isOpen={isDevicesModalOpen}
            onClose={closeDevicesModal}
          />
        )}
      </React.Suspense>

      {/* Bloqueio Visual para Manter Catálogo na Vertical em Celulares (CSS + JS) */}
      <div className={`catalog-orientation-guard ${isLandscapeMobileInCatalog && !hasActivePlayback ? '!flex' : ''}`}>
        <div className="w-16 h-16 rounded-2xl bg-theme-accent/20 border border-theme-accent/40 flex items-center justify-center text-theme-accent mb-4 shadow-2xl animate-pulse">
          <Smartphone className="w-8 h-8 rotate-90 animate-bounce" />
        </div>
        <h3 className="text-base font-black text-white mb-1.5 tracking-wide">
          Modo Catálogo na Vertical
        </h3>
        <p className="text-xs text-zinc-400 max-w-xs leading-relaxed mb-3">
          Gire seu aparelho para a posição <strong className="text-white">vertical (em pé)</strong> para navegar pelos canais, filmes e séries.
        </p>
        <div className="flex items-center gap-2 text-[10px] text-theme-accent bg-theme-accent/10 border border-theme-accent/30 px-3 py-1 rounded-full font-bold">
          <span>A tela cheia horizontal é liberada automaticamente ao reproduzir um vídeo</span>
        </div>
      </div>

      {/* Floating Smart TV Toast Notifications */}
      <ToastNotifications />
    </div>
  );
};

export default function App() {
  const checkRoutes = () => {
    if (typeof window !== 'undefined') {
      const p = window.location.pathname.toLowerCase();
      const h = window.location.hash.toLowerCase();
      // Só o endereço /tv (antes qualquer "tv" no # ou no ? abria o receptor por engano)
      const isTv = p === '/tv' || p.startsWith('/tv/');
      const isGerenciador = p.includes('gerenciador') || h.includes('gerenciador');
      return { isTv, isGerenciador };
    }
    return { isTv: false, isGerenciador: false };
  };

  const [routes, setRoutes] = useState(checkRoutes);

  useEffect(() => {
    const handleRouteUpdate = () => {
      setRoutes(checkRoutes());
    };
    window.addEventListener('popstate', handleRouteUpdate);
    window.addEventListener('hashchange', handleRouteUpdate);
    return () => {
      window.removeEventListener('popstate', handleRouteUpdate);
      window.removeEventListener('hashchange', handleRouteUpdate);
    };
  }, []);

  if (routes.isTv) {
    // O servidor entrega a pasta /tv direto; se o app abrir aqui (ex.: desenvolvimento), vai para a página da TV.
    if (typeof window !== 'undefined' && !window.location.pathname.toLowerCase().endsWith('/tv/index.html')) {
      window.location.replace('/tv/index.html');
    }
    return <div className="fixed inset-0 bg-black" />;
  }

  return (
    <IPTVProvider>
      {routes.isGerenciador ? (
        <React.Suspense fallback={<div className="fixed inset-0 bg-black flex items-center justify-center"><OpenTvLoader size="hero" glow="full" /></div>}>
          <GerenciadorPage />
        </React.Suspense>
      ) : (
        <IPTVAppContent />
      )}
    </IPTVProvider>
  );
}

